import { useEffect, useState } from 'react';

import { initiateDownload } from '#@/shared/utils/download';

import { MAX_FILE_SIZE_BYTES } from '../constants';
import {
  type Lightbox,
  type LightboxFile,
  type OpenLightboxOptions,
  type ScaleValues,
  STATUS,
} from '../types';
import { isFileExtensionSupported } from '../utils';

const isFilePreviewSupported = ({
  fileName,
  fileSizeBytes,
}: Pick<OpenLightboxOptions, 'fileName' | 'fileSizeBytes'>) =>
  isFileExtensionSupported(fileName) && (!fileSizeBytes || fileSizeBytes <= MAX_FILE_SIZE_BYTES);

const getType = (fileName: string): LightboxFile['type'] =>
  fileName.toLowerCase().endsWith('.pdf') ? 'document' : 'image';

export const useLightbox = (): Lightbox => {
  const [file, setFile] = useState<LightboxFile | null>(null);
  const [images, setImages] = useState<LightboxFile[] | null>(null);

  const [status, setStatus] = useState(STATUS.closed);
  const [isLoading, setIsLoading] = useState(false);

  const [title, setTitle] = useState<string | null>(null);
  const [initialScale, setInitialScale] = useState<ScaleValues | null>(null);
  const [shouldHideDownload, setShouldHideDownload] = useState(false);
  const [shouldHideOpenInNewTab, setShouldHideOpenInNewTab] = useState(false);

  const shouldBeOpen = Boolean(file) || Boolean(images);

  useEffect(() => {
    if (shouldBeOpen && status === STATUS.closed) {
      setStatus(STATUS.opening);
      return;
    }
    if (!shouldBeOpen && status === STATUS.open) {
      setStatus(STATUS.closing);
    }
  }, [shouldBeOpen, status]);

  const close = () => {
    setFile(null);
    setImages(null);
    setTitle(null);
    setShouldHideDownload(false);
    setShouldHideOpenInNewTab(false);
    setIsLoading(false);
  };

  const open = (options: OpenLightboxOptions) => {
    const { fileUrl, fileOpenInNewTabUrl, fileName, isSupported, onUnsupported, onOpened } =
      options;

    if (!isSupported && !isFilePreviewSupported(options)) {
      return onUnsupported ? onUnsupported() : initiateDownload(fileUrl);
    }

    setFile({
      url: fileUrl,
      openInNewTabUrl: fileOpenInNewTabUrl,
      name: fileName,
      type: getType(fileName),
    });

    setTitle(options.title ?? null);
    setInitialScale(options.initialScale ?? null);
    setShouldHideDownload(options.shouldHideDownload ?? false);
    setShouldHideOpenInNewTab(options.shouldHideOpenInNewTab ?? false);
    setIsLoading(true);

    onOpened?.();
  };

  const openImages = (options: OpenLightboxOptions[]) => {
    const supportedFiles = options
      .filter((fileOpts) => fileOpts.isSupported || isFilePreviewSupported(fileOpts))
      .map(({ fileUrl, fileOpenInNewTabUrl, fileName, onOpened }) => {
        onOpened?.();

        return {
          url: fileUrl,
          openInNewTabUrl: fileOpenInNewTabUrl,
          name: fileName,
          type: getType(fileName),
        };
      });

    const unsupportedFiles = options.filter(
      (fileOpts) => !fileOpts.isSupported && !isFilePreviewSupported(fileOpts),
    );

    unsupportedFiles.forEach(({ onUnsupported }) => onUnsupported?.());

    if (supportedFiles.length > 0) {
      setImages(supportedFiles);
      setIsLoading(true);
    }
  };

  return {
    open,
    openImages,
    close,
    setIsLoading,
    setStatus,
    file,
    images,
    title,
    initialScale,
    shouldHideDownload,
    shouldHideOpenInNewTab,
    isLoading,
    status,
  };
};
